/**
 * Permission Types & Role-Based Access Control
 */
const ADMIN_PERMISSIONS = [
    'dashboard:view',
    'patients:read', 'patients:create', 'patients:update', 'patients:delete', 'patients:export',
    'appointments:read', 'appointments:create', 'appointments:update', 'appointments:cancel',
    'leads:read', 'leads:create', 'leads:update', 'leads:archive', 'leads:convert',
    'campaigns:read', 'campaigns:create', 'campaigns:manage',
    'billing:view', 'billing:manage',
    'financial:read', 'financial:create', 'financial:manage',
    'inventory:read', 'inventory:manage',
    'reports:view', 'analytics:view', 'ai:view',
    'settings:view', 'settings:manage',
    'users:read', 'users:manage',
    'locations:read', 'locations:manage',
    'webhooks:manage', 'whatsapp:manage',
    'inbox:read', 'inbox:write', 'inbox:assign', 'inbox:manage', 'inbox:view-all',
    // Story 33.9: clínico (admin tem tudo)
    'anamnesis:read', 'anamnesis:fill', 'anamnesis:manage',
    'documents:read', // Story 47.3
    // 50.5 — admin assina e cancela sem trava de escopo (decisão do operador, 15/08).
    'documents:sign', 'documents:cancel',
    'clinical:read', 'clinical:manage',
    'photos:read', 'photos:manage',
    'inventory:deduct',
    // Story 33.9 C2 batch3: services/leads/appointments faltantes (admin tem tudo)
    'services:read', 'services:write', 'services:manage',
    'leads:manage',
    'appointments:write', 'appointments:delete',
    // Story 33.9 C2 catálogo: 45 perms novas (admin tem TODAS)
    'analytics:manage', 'audit:read', 'commissions:read', 'commissions:manage', 'commissions:pay', 'deletion:request', 'deletion:read', 'deletion:approve', 'deletion:deny', 'financial:delete', 'financial:manage-goals', 'financial:manage-fees', 'financial:cancel', 'message-templates:delete', 'inventory:create', 'inventory:update', 'inventory:delete', 'inventory:link-service', 'onboarding:view', 'onboarding:manage', 'professionals:create', 'professionals:update', 'professionals:delete', 'subscription:manage', 'upload:delete',
    'import:read', 'import:create', 'message-templates:create', 'message-templates:update', 'kanban:manage', 'locations:assign-patient', 'nps:send', 'upload:create',
    'financial:view', 'inbox:label', 'inbox:send', 'inbox:notes-write', 'inbox:notes-delete', 'patients:notes-write', 'patients:notes-delete', 'message-templates:read', 'kanban:read', 'locations:view', 'nps:read', 'professionals:read', 'webhooks:read', 'whatsapp:view', 'whatsapp:request-access',
    'marketing-funnels:read', 'marketing-funnels:manage', // Story 66.2
    'account:self',
];
const SECRETARY_PERMISSIONS = [
    'dashboard:view',
    'patients:read', 'patients:create', 'patients:update', 'patients:export',
    'appointments:read', 'appointments:create', 'appointments:update', 'appointments:cancel',
    'leads:read', 'leads:create', 'leads:update', 'leads:archive', 'leads:convert',
    'campaigns:read', 'campaigns:create', 'campaigns:manage',
    'billing:view', 'billing:manage',
    'financial:read', 'financial:create', 'financial:manage',
    'inventory:read', 'inventory:manage',
    'reports:view', 'analytics:view',
    // view-all: a secretária é a recepção — precisa ver conversas não-atribuídas
    // para poder atribuí-las (inbox:assign pressupõe visão completa).
    'inbox:read', 'inbox:write', 'inbox:assign', 'inbox:view-all',
    // Story 33.9: reads clínicos sem @Roles hoje são abertos a qualquer logado
    // (RolesGuard fail-open) → secretary mantém o read (mirror, sem reduzir).
    'anamnesis:read', 'clinical:read',
    'documents:read', // Story 47.3 — recepção entrega/baixa o termo assinado
    // 50.5 — a recepção colhe a assinatura no balcão e cancela sem trava de escopo
    // (decisão do operador, 15/08: só a profissional tem escopo).
    'documents:sign', 'documents:cancel',
    // Story 33.9 C2 batch3: serviços (read+write) e gestão da agenda (write) — @Roles(adm,sec)
    'services:read', 'services:write',
    'appointments:write',
    // Story 33.9 C2 catálogo: AS + ASP (secretary)
    'import:read', 'import:create', 'message-templates:create', 'message-templates:update', 'kanban:manage', 'locations:assign-patient', 'nps:send', 'upload:create',
    'financial:view', 'inbox:label', 'inbox:send', 'inbox:notes-write', 'inbox:notes-delete', 'patients:notes-write', 'patients:notes-delete', 'message-templates:read', 'kanban:read', 'locations:view', 'nps:read', 'professionals:read', 'webhooks:read', 'whatsapp:view', 'whatsapp:request-access',
    'marketing-funnels:read', 'marketing-funnels:manage', // Story 66.2
    'account:self',
];
const PROFESSIONAL_PERMISSIONS = [
    'dashboard:view',
    'patients:read',
    'appointments:read', 'appointments:update',
    // Classe B: a profissional gerencia a PRÓPRIA agenda — marca o retorno combinado
    // na cadeira, remarca e reserva o próprio horário (almoço, falta, curso). O recorte
    // "só a dela" é imposto no servidor (appointments.controller: professionalId
    // sobrescrito quando role === 'professional'; idem blocks), nunca só na tela.
    'appointments:create', 'appointments:write', 'appointments:cancel',
    'leads:read',
    'inbox:read',
    // Story 33.9: o profissional é o executor clínico — espelha @Roles(admin,prof)
    // de anamnese/sessões/fotos + dedução de estoque no atendimento, e os reads abertos.
    'anamnesis:read', 'anamnesis:fill',
    'documents:read', // Story 47.3
    // 50.5 — a profissional assina QUALQUER paciente que ela veja (AC5, decisão explícita)
    // e cancela SÓ as dela. A trava do cancelar é por DADO e está no service, não aqui:
    // a matriz responde "pode?", o escopo responde "de quem?".
    'documents:sign', 'documents:cancel',
    'clinical:read', 'clinical:manage',
    'photos:read', 'photos:manage',
    'inventory:deduct',
    // Story 33.9 C2 batch3: catálogo de serviços é leitura ampla (@Roles(adm,sec,prof))
    'services:read',
    // Story 33.9 C2 catálogo: ASP (professional)
    'financial:view', 'inbox:label', 'inbox:send', 'inbox:notes-write', 'inbox:notes-delete', 'patients:notes-write', 'patients:notes-delete', 'message-templates:read', 'kanban:read', 'locations:view', 'nps:read', 'professionals:read', 'webhooks:read', 'whatsapp:view', 'whatsapp:request-access',
    'account:self',
];
// Story 33.9: as 5 permissions cross-clínica do Owner (blueprint §3.2, bloco Owner-only).
const OWNER_CROSS_CLINIC_PERMISSIONS = [
    'clinics:read-all',
    'clinics:manage',
    'members:manage-all',
    'billing:manage-all',
    'analytics:view-all',
];
// Owner = superset de admin (todas as intra-clínica) + as 5 cross-clínica.
const OWNER_PERMISSIONS = [...ADMIN_PERMISSIONS, ...OWNER_CROSS_CLINIC_PERMISSIONS];
export const ROLE_PERMISSIONS = {
    admin: ADMIN_PERMISSIONS,
    secretary: SECRETARY_PERMISSIONS,
    professional: PROFESSIONAL_PERMISSIONS,
    owner: OWNER_PERMISSIONS,
};
export function hasPermission(role, permission) {
    return ROLE_PERMISSIONS[role].includes(permission);
}
export function getPermissions(role) {
    return ROLE_PERMISSIONS[role];
}
//# sourceMappingURL=permissions.js.map